/**
 * Service module for tracking user requests.
 *
 * This module handles communication with the Main Website API's
 * user request endpoint to track product usage and credit consumption.
 */

import { APP_CONFIG } from '../configs/config';
import { authenticatedFetch } from '../utils/authCookieHelpers';

/**
 * Creates a user request record for a product.
 *
 * @param {string} productCode - Product identifier (e.g., 'business_card_manager').
 * @param {Object} metadata - Optional metadata about the request.
 * @returns {Promise<Object>} - Response data from the user request API.
 * @throws {Error} - If the request fails or the server returns an error.
 */
export const createUserRequest = async (productCode, metadata = null) => {
  const payload = {
    product_code: productCode,
    metadata: metadata
  };

  const response = await authenticatedFetch(
    `${APP_CONFIG.MAIN_WEBSITE_API_URL}/user_request/`,
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || `Failed to create user request: ${response.status}`
    );
  }

  return await response.json();
};

/**
 * Track business card manager usage.
 *
 * @param {Object} metadata - Optional metadata about the business card operation.
 * @returns {Promise<Object>} - Response data from the user request API.
 */
export const trackBusinessCardUsage = async (metadata = null) => {
  return createUserRequest('business_card_manager', metadata);
};

